import React from 'react';
import { BlogLayout, H2, H3, P, UL, InfoBox, ScoreCard } from './BlogLayout';

const Blog4 = () => (
  <BlogLayout
    title="Website Security Checklist for Small Businesses in India (2026)"
    description="A practical 10-point security checklist every Indian small business website should follow in 2026. Protect your customers and avoid penalties."
    date="April 2026"
    readTime={9}
    category="Checklist"
  >
    <P>
      Small businesses in India are increasingly targeted by cyberattacks — not because they're high-value targets, but because attackers know they're under-protected. A 2024 report found that over 60% of small business websites in India had at least one critical vulnerability.
    </P>
    <P>
      The good news: most of these vulnerabilities are simple to fix. This checklist covers the 10 most important security measures every Indian small business website needs in 2026.
    </P>

    <H2>1. ✅ Valid SSL Certificate</H2>
    <P>
      Your website must run on HTTPS. If your address bar shows "Not Secure" or doesn't have a padlock, this is your first priority.
    </P>
    <UL items={[
      "Enable Let's Encrypt (free) through your hosting control panel",
      "Set up auto-renewal so it doesn't expire silently",
      'Redirect all HTTP traffic to HTTPS with a 301 redirect',
      'Fix any mixed content warnings (images or scripts loading over HTTP on an HTTPS page)',
    ]} />
    <InfoBox title="Why it matters for Indian businesses" color="#00FF94">
      Google India users see "Not Secure" warnings prominently in Chrome. With 95%+ of Indian internet users on Chrome or mobile browsers, a missing padlock directly costs you customers and conversions.
    </InfoBox>

    <H2>2. ✅ HTTP Security Headers</H2>
    <P>
      Add at minimum these four headers to your server configuration — they take under an hour and cost nothing:
    </P>
    <ScoreCard label="X-Frame-Options: SAMEORIGIN" score="Prevents clickjacking" color="#00FF94" />
    <ScoreCard label="X-Content-Type-Options: nosniff" score="Prevents MIME sniffing" color="#00FF94" />
    <ScoreCard label="Strict-Transport-Security" score="Enforces HTTPS always" color="#00FF94" />
    <ScoreCard label="Referrer-Policy" score="Prevents URL data leaks" color="#00FF94" />
    <P>
      Ask your developer or hosting provider to add these. If you're on WordPress, plugins like "Headers Security" can add them without any coding.
    </P>

    <H2>3. ✅ Keep Software Updated</H2>
    <P>
      Outdated software is the single biggest cause of Indian website hacks. This includes:
    </P>
    <UL items={[
      'WordPress core — update immediately when new versions are released',
      'All plugins and themes — outdated plugins account for over 50% of WordPress hacks',
      'PHP version — many Indian hosts still run PHP 7.x which is end-of-life. Upgrade to PHP 8.2+',
      'Server OS — ask your hosting provider about patch schedules',
    ]} />
    <InfoBox title="Auto-updates" color="#00F0FF">
      Enable automatic minor version updates for WordPress core and security-only updates for plugins. For major version updates, test in a staging environment first.
    </InfoBox>

    <H2>4. ✅ Strong Passwords and 2FA</H2>
    <P>
      "admin" and "password123" are still the most common credentials used on Indian websites. Attackers run automated scripts that try thousands of common passwords every hour.
    </P>
    <UL items={[
      'Use a password manager (Bitwarden is free and excellent)',
      'Every admin account should have a unique, randomly generated password of 20+ characters',
      'Enable Two-Factor Authentication (2FA) on your hosting account, domain registrar, and CMS',
      'Delete any unused admin accounts immediately',
      'Change default admin usernames — "admin" is the first thing attackers try',
    ]} />

    <H2>5. ✅ Regular Backups</H2>
    <P>
      A backup won't prevent an attack, but it means you can recover in hours instead of days (or never).
    </P>
    <UL items={[
      'Daily automated backups of both files and database',
      'Store backups in a separate location — not just on the same server',
      'Test your backup restoration at least once every 3 months',
      'Keep at least 30 days of backup history',
    ]} />
    <InfoBox title="Ransomware is rising in India" color="#FF0055">
      Indian websites are increasingly targeted by ransomware that encrypts your files and demands payment. A verified, offsite backup is your only reliable protection. Without it, you either pay the ransom or lose everything.
    </InfoBox>

    <H2>6. ✅ Secure Contact Forms and Input Fields</H2>
    <P>
      Every form on your website is a potential entry point for attackers. Unsecured forms are vulnerable to SQL injection, spam, and data harvesting.
    </P>
    <UL items={[
      'Add CAPTCHA (reCAPTCHA v3 is invisible and free) to all forms',
      'Validate and sanitise all user input on the server side — never trust what users send',
      'Use parameterised queries or an ORM for any database interactions',
      'Rate-limit form submissions to prevent spam and brute-force attacks',
    ]} />

    <H2>7. ✅ Secure Your DNS</H2>
    <P>
      DNS security protects your domain from being hijacked and your email from being spoofed. Three records to check:
    </P>
    <H3>SPF Record</H3>
    <P>Specifies which servers can send email from your domain. Without it, anyone can impersonate your business via email.</P>
    <H3>DMARC Record</H3>
    <P>Tells receiving mail servers what to do with emails that fail SPF/DKIM checks. Set at minimum to <code style={{ background: '#1a1a3e', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>p=quarantine</code>.</P>
    <H3>Domain Lock</H3>
    <P>Enable domain transfer lock at your registrar. This prevents someone from transferring your domain away from you without your explicit approval.</P>

    <H2>8. ✅ Monitor for Blacklisting</H2>
    <P>
      If your site gets hacked and serves malware, Google will blacklist it within hours. You won't know unless you're monitoring.
    </P>
    <UL items={[
      'Set up Google Search Console — it sends email alerts if your site is flagged',
      "Check Google's Safe Browsing report for your domain monthly",
      "Use WebShield's automated scan to check blacklist status alongside your other security checks",
      "If blacklisted: clean the malware, submit for review through Search Console, and expect 1–3 days to be cleared",
    ]} />

    <H2>9. ✅ Payment Security (if applicable)</H2>
    <P>
      If you accept payments on your Indian website, additional requirements apply:
    </P>
    <UL items={[
      'Use a trusted payment gateway — Razorpay, PayU, CCAvenue — never collect card details on your own server',
      "Display your payment provider's trust badges prominently",
      'Never log or store payment information in your database',
      'Add HTTPS everywhere on your checkout flow — not just the payment page',
      'If using Razorpay or similar: verify webhook signatures to prevent fake payment notifications',
    ]} />
    <InfoBox title="Indian regulations" color="#7000FF">
      The Reserve Bank of India (RBI) has guidelines for payment security. The Information Technology Act 2000 and its amendments create legal liability for Indian businesses that mishandle customer data. A data breach can result in penalties and legal action.
    </InfoBox>

    <H2>10. ✅ Run a Security Scan Regularly</H2>
    <P>
      Security isn't a one-time setup — it's an ongoing process. New vulnerabilities are discovered every week. Software updates can accidentally introduce new problems. Your hosting provider might change something on their end.
    </P>
    <P>Run a security scan on your website at minimum:</P>
    <UL items={[
      'After any major software update or deployment',
      'After adding any new plugin, extension, or third-party script',
      'Monthly as a routine check',
      'Immediately if you notice anything suspicious (unexpected traffic, slow performance, user complaints)',
    ]} />

    <H2>Quick Priority Summary</H2>
    <P>If you haven't done any of these yet, fix them in this order:</P>
    <ScoreCard label="🔴 Critical — Fix today" score="SSL, strong passwords, 2FA" color="#FF0055" />
    <ScoreCard label="🟡 High — Fix this week" score="Security headers, software updates, backups" color="#FFD600" />
    <ScoreCard label="🟢 Medium — Fix this month" score="DNS security, form protection, blacklist monitoring" color="#00FF94" />
    <ScoreCard label="🔵 Ongoing — Monthly" score="Security scan, payment audit, access review" color="#00F0FF" />

    <P>
      A WebShield scan gives you a personalised version of this checklist — showing exactly which of these items your specific website passes or fails, with a score and detailed recommendations in a downloadable PDF report.
    </P>
  </BlogLayout>
);

export default Blog4;